// Board interaction utilities - coordinate conversion and event handling

const FILES = 'abcdefgh';

/**
 * Convert pixel coordinates to a chess square
 * 
 * @param {number} x - X coordinate relative to board
 * @param {number} y - Y coordinate relative to board
 * @param {number} boardWidth - Width of the board in pixels
 * @param {string} orientation - Board orientation ('white' or 'black')
 * @returns {string|null} Square notation (e.g., 'e4') or null if out of bounds
 */
export function coordsToSquare(x, y, boardWidth, orientation = 'white') {
  const squareSize = boardWidth / 8;
  const colIndex = Math.floor(x / squareSize);
  const rowIndex = Math.floor(y / squareSize);
  
  if (colIndex < 0 || colIndex > 7 || rowIndex < 0 || rowIndex > 7) {
    return null;
  }
  
  if (orientation === 'black') {
    // Invert for black orientation
    const file = FILES[7 - colIndex];
    const rank = rowIndex + 1;
    return file + rank;
  }
  
  const file = FILES[colIndex];
  const rank = 8 - rowIndex;
  return file + rank;
}

/**
 * Convert a square notation to pixel coordinates (center of square)
 * 
 * @param {string} square - Square notation (e.g., 'e4')
 * @param {number} boardWidth - Width of the board in pixels
 * @param {string} orientation - Board orientation ('white' or 'black')
 * @returns {{ x: number, y: number }|null} Pixel coordinates or null if invalid
 */
export function squareToCoords(square, boardWidth, orientation = 'white') {
  if (!square || square.length !== 2) return null;
  
  const file = square[0].toLowerCase();
  const rank = parseInt(square[1], 10);
  
  const fileIndex = FILES.indexOf(file);
  if (fileIndex === -1 || rank < 1 || rank > 8) return null;
  
  const squareSize = boardWidth / 8;
  
  if (orientation === 'black') {
    const x = (7 - fileIndex) * squareSize + squareSize / 2;
    const y = (rank - 1) * squareSize + squareSize / 2;
    return { x, y };
  }
  
  const x = fileIndex * squareSize + squareSize / 2;
  const y = (8 - rank) * squareSize + squareSize / 2;
  return { x, y };
}

/**
 * Check if coordinates are within board bounds
 * 
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {number} boardWidth - Width of the board
 * @param {number} boardHeight - Height of the board (defaults to boardWidth)
 * @returns {boolean} True if within bounds
 */
export function isWithinBoard(x, y, boardWidth, boardHeight = boardWidth) {
  return x >= 0 && y >= 0 && x <= boardWidth && y <= boardHeight;
}

/**
 * Get mouse/touch position relative to board element
 * 
 * @param {MouseEvent|TouchEvent} event - Mouse or touch event
 * @param {HTMLElement} boardElement - Board DOM element
 * @returns {{ x: number, y: number }} Coordinates relative to board
 */
export function getEventBoardPosition(event, boardElement) {
  const rect = boardElement.getBoundingClientRect();
  const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
  const clientY = event.clientY ?? event.touches?.[0]?.clientY ?? 0;
  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  };
}

/**
 * Create a board context menu handler
 * This is a factory that creates a handler which needs component callbacks
 * 
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.getPhase - Returns current game phase
 * @param {Function} callbacks.getGamePiece - Returns piece at square
 * @param {Function} callbacks.removePiece - Removes piece from board
 * @param {Function} callbacks.getUnitId - Gets unit ID for square
 * @param {Function} callbacks.updateUnitMap - Updates unit ID mapping
 * @param {Function} callbacks.addToBench - Adds piece to bench
 * @returns {Function} Event handler function
 */
export function createBoardContextMenuHandler(callbacks) {
  const { getPhase, getGamePiece, removePiece, getUnitId, updateUnitMap, addToBench } = callbacks;
  
  return function onBoardContextMenu(e) {
    e.preventDefault();
    if (getPhase() !== 'prep') return;
    
    const boardElement = e.currentTarget || document.getElementById('grotto-board');
    if (!boardElement) return;
    
    const rect = boardElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (!isWithinBoard(x, y, rect.width, rect.height)) return;
    
    const square = coordsToSquare(x, y, rect.width, 'white');
    if (!square) return;
    
    const piece = getGamePiece(square);
    if (!piece) return;
    if (piece.color !== 'w') return;
    if (piece.type === 'k') return; // Can't remove king
    
    // Transfer identity to bench
    const pieceType = 'w' + piece.type.toUpperCase();
    const unitId = getUnitId(square);
    removePiece(square);
    if (unitId) {
      updateUnitMap(square, null);
    }
    addToBench(pieceType, unitId);
  };
}






